/**
 * RepairBay — mechanical (repair) drones. They deploy exactly like mining drones
 * (out the hull front, or the Mk III side gates), fly to a ring around the hull,
 * hover and beam the ship with GREEN twin beams that repair HP (the GameState
 * charges 3 minerals per HP). They reuse the DroneUnit shape so the existing
 * DroneField renders them. They stand down during combat or when toggled off.
 */
import { Vector2 } from "@/math/Vector2";
import { MINING_DRONE } from "@/config/balance";
import type { DroneUnit } from "./DroneBay";

export interface RepairBayCtx {
  active: boolean; // unlocked && toggled on && hull needs repair && not in combat
  shipPos: Vector2;
  hangar: Vector2;
  hangarFacing: Vector2;
  gateLeft?: Vector2;
  gateRight?: Vector2;
  maxDrones: number;
  speed: number;
  hullRadius: number; // spread of beam spots across the hull
}

export class RepairBay {
  readonly units: DroneUnit[] = [];
  private nextId = 100000; // separate id space from mining drones
  private spawnTimer = 0;

  /** How many drones are locked on and actively beaming the hull this frame. */
  get beaming(): number {
    let n = 0;
    for (const u of this.units) if (u.state === "attacking") n++;
    return n;
  }

  reset(): void {
    this.units.length = 0;
    this.spawnTimer = 0;
  }

  update(dt: number, ctx: RepairBayCtx): void {
    this.spawnTimer -= dt;
    if (ctx.active && this.spawnTimer <= 0 && this.units.length < ctx.maxDrones) {
      // Two side gates → launch a repair drone from each at once.
      if (ctx.gateLeft && ctx.gateRight) {
        this.units.push(this.spawn(ctx, ctx.gateLeft));
        if (this.units.length < ctx.maxDrones) {
          this.units.push(this.spawn(ctx, ctx.gateRight));
        }
      } else {
        this.units.push(this.spawn(ctx));
      }
      this.spawnTimer = MINING_DRONE.spawnInterval;
    }
    for (let i = this.units.length - 1; i >= 0; i--) {
      if (!this.updateDrone(this.units[i]!, dt, ctx)) this.units.splice(i, 1);
    }
  }

  private ringOffset(): Vector2 {
    const a = Math.random() * Math.PI * 2;
    const r = 55 + Math.random() * 45; // orbit ring around the hull
    return new Vector2(Math.cos(a) * r, Math.sin(a) * r);
  }

  /** A random spot ON the hull for this drone to beam (so drones target varied
   * points, not all the same centre). Relative to the ship centre. */
  private hullSpot(hullRadius: number): Vector2 {
    const a = Math.random() * Math.PI * 2;
    const r = Math.sqrt(Math.random()) * hullRadius * 0.7;
    return new Vector2(Math.cos(a) * r, Math.sin(a) * r);
  }

  private spawn(ctx: RepairBayCtx, gate?: Vector2): DroneUnit {
    let lpx = ctx.hangar.x;
    let lpy = ctx.hangar.y;
    let ldx = ctx.hangarFacing.x;
    let ldy = ctx.hangarFacing.y;
    if (gate) {
      lpx = gate.x;
      lpy = gate.y;
      const ox = gate.x - ctx.hangar.x;
      const oy = gate.y - ctx.hangar.y;
      const len = Math.hypot(ox, oy) || 1;
      ldx = ox / len;
      ldy = oy / len;
    }
    return {
      id: this.nextId++,
      pos: new Vector2(lpx, lpy),
      vel: new Vector2(ldx * ctx.speed, ldy * ctx.speed),
      heading: Math.atan2(ldy, ldx),
      cargo: 0,
      frame: Math.random(),
      state: "launching",
      targetId: "",
      firingOffset: this.ringOffset(),
      attackOffset: this.hullSpot(ctx.hullRadius),
      firingPos: new Vector2(),
      attackPoint: new Vector2(),
      spotTimer: 0,
      launchTimer: 1, // ~1s straight out the gate/front, then curve to the ring
      beamTarget: null,
      floatAccum: 0,
      floatTimer: 0,
      moveTime: 0,
      resetTimer: 0,
    };
  }

  private nearestGate(u: DroneUnit, ctx: RepairBayCtx): Vector2 {
    const gl = ctx.gateLeft!;
    const gr = ctx.gateRight!;
    const dl = (u.pos.x - gl.x) ** 2 + (u.pos.y - gl.y) ** 2;
    const dr = (u.pos.x - gr.x) ** 2 + (u.pos.y - gr.y) ** 2;
    return dl <= dr ? gl : gr;
  }

  private updateDrone(u: DroneUnit, dt: number, ctx: RepairBayCtx): boolean {
    const spd = ctx.speed;

    if (u.state === "launching") {
      u.launchTimer -= dt;
      u.pos.x += u.vel.x * dt;
      u.pos.y += u.vel.y * dt;
      if (u.launchTimer <= 0) u.state = "movingToSpot";
      return true;
    }

    // Stand down: fly back to the nearest gate / hangar and despawn on arrival.
    if (!ctx.active) {
      u.beamTarget = null;
      const dock =
        ctx.gateLeft && ctx.gateRight ? this.nearestGate(u, ctx) : ctx.hangar;
      return !this.steer(u, dock, dt, spd, 12);
    }

    // Firing ring position tracks the (moving) hull.
    u.firingPos.set(ctx.shipPos.x + u.firingOffset.x, ctx.shipPos.y + u.firingOffset.y);

    if (u.state === "movingToSpot") {
      u.beamTarget = null;
      if (this.steer(u, u.firingPos, dt, spd, 6)) {
        u.state = "attacking";
        u.spotTimer = 2 + Math.random() * 2;
      }
      return true;
    }

    // attacking → hover on the ring, face + beam this drone's own hull spot (each
    // drone targets a different point, tracking the moving hull).
    u.vel.set(0, 0);
    u.attackPoint.set(ctx.shipPos.x + u.attackOffset.x, ctx.shipPos.y + u.attackOffset.y);
    u.heading = Math.atan2(u.attackPoint.y - u.pos.y, u.attackPoint.x - u.pos.x);
    u.beamTarget = u.attackPoint;
    u.spotTimer -= dt;
    if (u.spotTimer <= 0) {
      u.firingOffset = this.ringOffset();
      u.attackOffset = this.hullSpot(ctx.hullRadius); // re-pick a fresh hull spot
      u.state = "movingToSpot";
    }
    return true;
  }

  /** Steer toward `goal`; returns true once within `arrive`. */
  private steer(
    u: DroneUnit,
    goal: Vector2,
    dt: number,
    spd: number,
    arrive: number,
  ): boolean {
    const dx = goal.x - u.pos.x;
    const dy = goal.y - u.pos.y;
    if (dx * dx + dy * dy <= arrive * arrive) return true;
    const desired = Math.atan2(dy, dx);
    let diff = desired - u.heading;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    const maxTurn = MINING_DRONE.turnRate * dt;
    u.heading += diff > maxTurn ? maxTurn : diff < -maxTurn ? -maxTurn : diff;
    const vx = Math.cos(u.heading) * spd;
    const vy = Math.sin(u.heading) * spd;
    u.vel.set(vx, vy);
    u.pos.x += vx * dt;
    u.pos.y += vy * dt;
    return false;
  }
}
